import { Duration } from 'luxon';

import { parseUtcDate } from './date';
import { Reply } from '~/types/models/reply';

export function ifNotNull<V, N extends () => R, R>(
  maybeNull: V | null,
  notNullValue: N
): R;
export function ifNotNull<V, N, R = N>(maybeNull: V | null, notNullValue: N): R;
export function ifNotNull<V, N>(maybeNull: V | null, notNullValue: N) {
  if (typeof notNullValue === 'function') {
    return maybeNull != null ? notNullValue() : null;
  }

  return maybeNull != null ? notNullValue : null;
}

export function removeNullKeys<T = Record<string, unknown>>(
  obj: T
): Partial<T> {
  return Object.keys(obj).reduce<Partial<T>>((newObj, key) => {
    if (obj[key] != null) {
      newObj[key] = obj[key];
    }

    return newObj;
  }, {});
}

export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

function getLastMessage(reply: Reply) {
  const utcDate = parseUtcDate(reply.created_at);

  return utcDate.toRelative({
    style: 'short',
  });
}

export type ConsecutivePartitionPredicate<V> = (a: V, b: V) => boolean;

export function consecutivePartition<V>(
  collection: V[],
  predicate: ConsecutivePartitionPredicate<V>
) {
  if (collection.length === 0) {
    return [];
  }

  const partitions: V[][] = [];
  let current = [collection[0]];

  for (let i = 1; i < collection.length; i++) {
    if (predicate(current[0], collection[i])) {
      current.push(collection[i]);
    } else {
      partitions.push(current);
      current = [collection[i]];
    }
  }

  partitions.push(current);

  return partitions;
}

export function groupReplies(replies: Reply[], groupByTimestamp: boolean) {
  const isGroup = (a: Reply, b: Reply) => {
    return a.author.id === b.author.id && !b.media && !a.media;
  };

  let comparator: ConsecutivePartitionPredicate<Reply>;
  if (groupByTimestamp) {
    comparator = (a, b) =>
      isGroup(a, b) && getLastMessage(a) === getLastMessage(b);
  } else {
    comparator = isGroup;
  }

  return consecutivePartition(replies, comparator).map((replies) => {
    const { author } = replies[0];

    return {
      author,
      replies,
      lastMessage: getLastMessage(replies[replies.length - 1]),
    };
  });
}

export const padZeros = (n: number) => {
  const s = String(n);
  return s.length === 1 ? `0${s}` : s;
};

export interface ElapsedOptions {
  noPadMinutes: boolean;
}

export function elapsed(
  input: number | Duration,
  options?: Partial<ElapsedOptions>
) {
  const duration = Duration.isDuration(input)
    ? input
    : Duration.fromObject({ seconds: input });

  const shouldPadMinutes = !options?.noPadMinutes;
  const format = shouldPadMinutes ? 'mm:ss' : 'm:ss';

  return duration.toFormat(format);
}

export function elipsis(s: string, n: number) {
  let truncated = s.substr(0, n);
  if (s.length > n) {
    truncated += '...';
  }
  return truncated;
}

export async function sleep(timeout: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, timeout));
}

export async function sequentialMap<T>(
  resolvables: (() => Promise<T>)[]
): Promise<T[]> {
  const results: T[] = [];
  for (const resolvable of resolvables) {
    results.push(await resolvable());
  }
  return results;
}
